
const BASE_URL = process.env.REACT_APP_BASE_URL_2;

/**
 * Templates API - Mirrors the Designs API for managing reusable base templates
 */
const overlayApi = {
  /**
   * Create a new template
   * @param {Object} data - Template data { name, canvas_data, ... }
   * @returns {Promise<Object>} Created template object
   */
  createTemplate: async (data) => {
    const res = await fetch(`${BASE_URL}/api/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.message || `Failed to create template: ${res.statusText}`);
    }
    return res.json();
  },

  /**
   * List templates
   * @param {number} limit - Number of items (default 50)
   * @param {number} offset - Pagination offset (default 0)
   * @returns {Promise<Array>} Array of template objects
   */
  getTemplates: async (limit = 50, offset = 0) => {
    const res = await fetch(`${BASE_URL}/api/templates?limit=${limit}&offset=${offset}`);
    if (!res.ok) {
        throw new Error(`Failed to list templates: ${res.statusText}`);
    }
    const data = await res.json();
    // Adapter: Frontend expects { templates: [...] } but API returns [...]
    return Array.isArray(data) ? { templates: data } : data;
  },

  /**
   * Get a single template by ID
   * @param {number} id - Template ID
   * @returns {Promise<Object>} Template data
   */
  getTemplate: async (id) => {
    const res = await fetch(`${BASE_URL}/api/templates/${id}`);
    if (!res.ok) {
        throw new Error(`Failed to get template: ${res.statusText}`);
    }
    return res.json();
  },

  /**
   * Update a template
   * @param {number} id - Template ID
   * @param {Object} updates - Template updates
   * @returns {Promise<Object>} Updated template
   */
  updateTemplate: async (id, updates) => {
    const res = await fetch(`${BASE_URL}/api/templates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
        throw new Error(`Failed to update template: ${res.statusText}`);
    }
    return res.json();
  },

  /**
   * Delete a template
   * @param {number} id - Template ID
   * @returns {Promise<Object>} { success: true, message: "Template deleted" }
   */
  deleteTemplate: async (id) => {
    const res = await fetch(`${BASE_URL}/api/templates/${id}`, { method: 'DELETE' });
    if (!res.ok) {
        throw new Error(`Failed to delete template: ${res.statusText}`);
    }
    return res.json();
  },

  /**
   * Duplicate a template (Client-side implementation via Get + Create)
   * @param {number} id - Template ID to duplicate
   * @returns {Promise<Object>} Duplicated template
   */
  duplicateTemplate: async (id) => {
    try {
        const template = await overlayApi.getTemplate(id);
        const newTemplateData = {
            name: template.name ? `${template.name} (Copy)` : 'Untitled Template (Copy)',
            canvas_data: template.canvas_data || template.json || {} // Handle different data shapes if necessary
        };
        return await overlayApi.createTemplate(newTemplateData);
    } catch (error) {
        throw new Error(`Failed to duplicate template: ${error.message}`);
    }
  }
};

// Export individual functions for backward compatibility
export const {
  createTemplate,
  getTemplates,
  getTemplate,
  updateTemplate,
  deleteTemplate,
  duplicateTemplate
} = overlayApi;

export default overlayApi;
